import {
    ChangeDetectionStrategy,
    Component,
    Input,
    inject,
    OnInit,
    signal,
    computed,
    ViewEncapsulation,
    input,
} from '@angular/core';
import { FiTagsListPositionTypes } from '@fi-app/components/items-list/enums';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import { IDocumentModel } from '@fi-app/document/document';
import { FiItemsListType } from '@fi-app/shared/fi-items-list/fi-items-list-type';
import { ILoadDataArgs } from '@fi-app/shared/fi-items-list/fi-data-source-base.directive';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { IItemsListFilter } from '@fi-app/shared/fi-items-list/fi-items-list-filter/fi-items-list-filter/fi-items-list-filter';
import { ModuleType } from '@fi-app/shared/types/module-type';
import { FiItemsListFilterService } from '@fi-app/shared/fi-items-list/fi-items-list-filter/fi-items-list-filter.service';
import { DocumentService } from '../document.service';
import { IItemsListAction2, ISideListOptionsItem } from '@fi-shared/fi-items-list/fi-items-list';
import { SideListOptionType } from '@fi-app/shared/types/side-list-option-type';
import { getSideListOptionsBottomFn } from '@fi-shared/fi-items-list/side-list-options';
import { fiItemsListTypeNameTextId } from '@fi-shared/utils/fi-items-list-type-name-text-id';
import { AccessToApplicationTypeService } from '@fi-shared/fi-shared-services/access-to-application-type.service';
import { ApplicationSettingsType } from '@fi-app/shared/types/application-settings-type';
import { AccessRightType } from '@fi-app/shared/types/access-right-type';
import { takeUntil } from 'rxjs/operators';
import { IPreviewImageCellValue } from '@fi-app/shared/fi-wrapper-table/fi-wrapper-table';
import { FiMediaStorageService } from '@fi-shared/media-bank/fi-media-storage.service';
import { FiImageSize } from '@fi-app/shared/types/image-size';
import { FiAppSettings } from '@fi-shared/fi-app-settings';
import { ILightWeightObject } from '@fi-shared/models';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { getObjectTypeNameTextId } from '@fi-app/shared/utils/object-type-name';
import { FiDisplayType } from '@fi-app/shared/types/display-type';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { isDefined } from '@fi-shared/utils/is-defined';

@Component({
    selector: 'fi-document-list',
    templateUrl: './document-list.component.html',
    styleUrls: ['./document-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-document-list' },
    standalone: false,
})
export class DocumentListComponent extends FiRemoteStateFullDataSource<IDocumentModel> implements OnInit {
    public preselectedTagId = input<number | undefined>(undefined);
    public isItemActionsShown = input<boolean>(true);
    public hasCheckedColumn = input<boolean>(true);
    public allowMultiSelect = input<boolean>(true);

    public isFavoriteIconShown = input<boolean>(true);
    public displayType = input<FiDisplayType>(FiDisplayType.List);

    protected isListLayout = computed(() => this.displayType() === FiDisplayType.List);

    private itemsListFilterService = inject(FiItemsListFilterService);
    private documentService = inject(DocumentService);
    private accessToApplicationTypeService = inject(AccessToApplicationTypeService);
    private fiMediaStorageService = inject(FiMediaStorageService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);
    private fiUserDataService = inject(FiUserDataService);

    public defaultSortColumnName = 'PublicationStartDate';
    public defaultSortDirection = true;
    public objectType = ObjectTypes.Document;
    public itemsListType = FiItemsListType.Documents;

    protected isSuperAdminAccess = signal<boolean>(
        this.itemsListFilterService.getIsSuperAdminAccess(ModuleType.Document),
    );
    protected isFilterSettingsShown = this.itemsListFilterService.getIsFilterSettingsShown(ModuleType.Document);
    protected currentFilter = signal<IItemsListFilter>(this.itemsListFilterService.getFilter(ModuleType.Document));

    protected itemsListActions = signal<IItemsListAction2[]>([]);
    protected isNameColumnOffsetReset = computed(
        () => this.hasCheckedColumn() || this.isFavoriteIconShown() || this.isSuperAdminAccess(),
    );

    protected readonly FiItemsListType = FiItemsListType;

    protected readonly isBrandProfileShown = this.fiUserDataService.getUserBusinessUnits().length > 1;

    protected readonly documentOptionsTop: ISideListOptionsItem[] = [
        { value: SideListOptionType.SharedWithMe, text: this.appTextService.get('common.shared_with_me') },
        { value: SideListOptionType.New, text: this.appTextService.get('new_pages') },
        { value: SideListOptionType.OnlyNotVisited, text: this.appTextService.get('document.unread_pages') },
        { value: SideListOptionType.CreatedBy, text: this.appTextService.get('common.created_by_me') },
        { value: SideListOptionType.Favorite, text: this.appTextService.get('common.my_favorites') },
        { value: SideListOptionType.Deleted, text: this.appTextService.get('common.trash') },
    ];
    protected readonly documentOptionsBottom: ISideListOptionsItem[] = getSideListOptionsBottomFn(this.appTextService);
    protected readonly documentOptionsTags = signal<ISideListOptionsItem[]>([]);
    protected readonly hasSuperAdminAccess = this.accessToApplicationTypeService.HasAccessToApplicationType(
        ApplicationSettingsType.DocumentGlobal,
        AccessRightType.SuperAdmin,
    );

    private readonly restoreListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.restore'),
        onClick: () => this.restoreSelectedItems(),
    };

    private readonly moveToTrashListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.delete'),
        onClick: () => this.confirmMoveToTrashItems(),
    };

    private readonly deletePermanentlyListAction: Readonly<IItemsListAction2> = {
        title: this.appTextService.get('common.delete_permanently'),
        onClick: () => this.confirmDeletePermanentlySelectedItems(),
    };

    public ngOnInit(): void {
        this.setAvailableTags();
        this.setItemsListActions();
        this.documentService.previewImageGenerated$.pipe(takeUntil(this.ngOnDestroy$)).subscribe((documentId) => {
            this.updateItems((items) => {
                return items.map((item) => {
                    if (item.Id === documentId) {
                        item.ChangedOn = new Date().toISOString();
                        return item;
                    }
                    return item;
                });
            });
        });
        if (isDefined(this.preselectedTagId())) {
            this.selectedTagId = this.preselectedTagId();
            this.updateFilterTags([this.selectedTagId]);
            this.onFilterSettingsChanged();
            this.onItemsListFilterChanged(this.currentFilter());
        }
    }

    private updateFilterTags(tagIds: number[]): void {
        this.currentFilter.update((cf) => ({
            ...cf,
            Settings: {
                ...cf.Settings,
                TagIds: tagIds,
            },
        }));
    }

    private setItemsListActions(): void {
        if (this.filterType === SideListOptionType.Deleted) {
            this.itemsListActions.set([this.restoreListAction, this.deletePermanentlyListAction]);
        } else {
            this.itemsListActions.set([this.moveToTrashListAction]);
        }
    }

    private confirmMoveToTrashItems(): void {
        this.fiItemsDeleteHelperService
            .documentDeleteConfirm(this.selectedItems())
            .then(() => this.moveToTrashSelectedItems());
    }

    private moveToTrashSelectedItems(): void {
        const selectedItems = this.selectedItems();
        this.documentService.deleteDocuments(selectedItems.map((item) => item.Id)).then(() => {
            this.removeData(selectedItems);
            this.clearSelectedItems();
            this.showItemsMovedToTrashSnackbar(selectedItems.length);
        });
    }

    private showItemsMovedToTrashSnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.moved_to_trash').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    private restoreSelectedItems(): void {
        this.fiItemsDeleteHelperService.restoreItems({
            itemsToRestore: this.selectedItems(),
            objectType: this.objectType,
            restoreFn: () => this.documentService.RestoreDocuments(this.selectedItems().map((item) => item.Id)),
            afterRestoreFn: (restoredItems) => {
                this.removeData(restoredItems);
                this.showItemsRestoredSnackbar(restoredItems.length);
                this.clearSelectedItems();
            },
        });
    }

    private showItemsRestoredSnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.restored').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    private confirmDeletePermanentlySelectedItems(): void {
        this.fiItemsDeleteHelperService
            .documentDeleteConfirm(this.selectedItems(), this.appTextService.get('document.delete_permanently_confirm'))
            .then(() => this.deletePermanentlySelectedItems());
    }

    private deletePermanentlySelectedItems(): void {
        const selectedItems = this.selectedItems();
        this.documentService.deleteDocuments(selectedItems.map((item) => item.Id)).then(() => {
            this.removeData(selectedItems);
            this.clearSelectedItems();
            this.showItemsDeletedPermanentlySnackbar(selectedItems.length);
        });
    }

    private showItemsDeletedPermanentlySnackbar(itemsCount: number): void {
        const snackbarTextMessage =
            itemsCount +
            ' ' +
            this.appTextService.get(getObjectTypeNameTextId(this.objectType, itemsCount !== 1)) +
            ' ' +
            this.appTextService.get('history.deleted').toLowerCase();

        this.snackBar.showSnackbar({
            textMessage: snackbarTextMessage,
            buttonText: this.appTextService.get('common.dismiss'),
        });
    }

    protected getDocumentImageCellValue(documentModel: IDocumentModel): IPreviewImageCellValue {
        return {
            image: this.fiMediaStorageService.getMediaFileUrl(
                {
                    PhysicalFileName: documentModel.PreviewFileName,
                    Extension: FiAppSettings.DefaultMediaBankImageExtension,
                    ChangedOn:
                        typeof documentModel.ChangedOn === 'string'
                            ? documentModel.ChangedOn
                            : documentModel.ChangedOn.toISOString(),
                },
                { size: this.isListLayout() ? FiImageSize.Thumbnail : FiImageSize.Small },
            ),
            imageLarge: '',
            iconClass: '',
        };
    }

    protected tagsLoading = signal(false);
    private setAvailableTags(): void {
        this.tagsLoading.set(true);
        this.documentService
            .GetDocumentTags()
            .then((tags) => {
                const documentOptionsTags = tags.map((tag) => {
                    const result: ISideListOptionsItem = {
                        value: tag.Id,
                        text: tag.Name,
                        Level: tag.Level,
                        ParentId: tag.ParentId,
                        IsDisabled: tag.IsDisabled,
                    };
                    return result;
                });
                this.documentOptionsTags.set(documentOptionsTags);
            })
            .finally(() => {
                this.tagsLoading.set(false);
            });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs) {
        const parameter = this.getItemsListParameter(loadArgs);

        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        parameter.IsSuperAdminAccess = this.isSuperAdminAccess();
        parameter.FilterSettings = this.currentFilter().Settings;
        if (this.selectedTagId) {
            parameter.TagIds = [this.selectedTagId];
        }
        return this.documentService.getDocumentsListData(parameter);
    }

    protected getItemsCountText(): string {
        let itemsCountText: string;
        const fetchedItemsCount = this.totalCount;
        if (this.loading() && !fetchedItemsCount) {
            itemsCountText = '?';
        } else if (fetchedItemsCount < FiAppSettings.NumberOfRecordsPerPage) {
            itemsCountText = fetchedItemsCount.toString();
        } else {
            itemsCountText = FiAppSettings.NumberOfRecordsPerPage.toString() + '+';
        }

        return `${itemsCountText} ${this.appTextService
            .get(fiItemsListTypeNameTextId(FiItemsListType.Documents, fetchedItemsCount))
            .toLowerCase()}`;
    }

    protected onSuperAdminToggleChange($event: boolean): void {
        this.isSuperAdminAccess.set($event);
        this.onFilterSettingsChanged();
        this.onItemsListFilterChanged(this.currentFilter());
    }

    protected onItemsListFilterChanged($event: IItemsListFilter): void {
        this.onFilterSettingsChanged();

        this.currentFilter.set($event);
        const filterType =
            $event.Settings.CategoryFilterTypes.concat($event.Settings.ChangedOnFilterType)[0] ??
            SideListOptionType.SharedWithMe;
        this.onSideListOptionsChanged(filterType);
        this.setItemsListActions();
        this.clearSelectedItems();
    }

    protected onFilterSettingsChanged(): void {
        this.itemsListFilterService.onFilterSettingsChanged(
            ModuleType.Document,
            this.currentFilter().Settings,
            this.isSuperAdminAccess(),
            this.isFilterSettingsShown,
        );
    }

    protected onTagClick(tagId: number): void {
        const currentTagIds = this.currentFilter().Settings.TagIds;
        if (currentTagIds.includes(tagId)) {
            return;
        }
        this.updateFilterTags([...currentTagIds, tagId]);
        this.onItemsListFilterChanged(this.currentFilter());
    }

    protected getDocumentPublicationStatusClass(
        isItemNotLoaded: boolean,
        isPublished: boolean,
        isDeleted: boolean,
    ): string {
        if (isItemNotLoaded) return '';

        if (isDeleted) return 'fi-cell-red';

        if (isPublished) return 'fi-cell-green';

        return 'fi-cell-orange';
    }

    protected getDocumentPublicationValue(isPublished: boolean, isDeleted: boolean): string {
        if (isDeleted) return this.appTextService.get('document.deleted');
        if (isPublished) return '';
        return this.appTextService.get('common.not_shared');
    }
}
